import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
} from "@angular/core";
import { provideRouter } from "@angular/router";

import { DatePipe, registerLocaleData } from "@angular/common";
import {
  HttpClientModule,
  provideHttpClient,
  withInterceptors,
} from "@angular/common/http";
import en from "@angular/common/locales/en";
import { FormsModule } from "@angular/forms";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideEffects } from "@ngrx/effects";
import { provideRouterStore } from "@ngrx/router-store";
import { provideStore } from "@ngrx/store";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { KeycloakAngularModule, KeycloakService } from "keycloak-angular";
import { BlockUIModule } from "ng-block-ui";
import { en_US, provideNzI18n } from "ng-zorro-antd/i18n";
import { MessageService } from "primeng/api";
import { routes } from "./app.routes";
import { AgentEffects } from "./core/ngrx/effects/agent.effects";
import { ApplicationDetailsEffects } from "./core/ngrx/effects/application-details.effects";
import { AutomationEffects } from "./core/ngrx/effects/automation.effects";
import { ConnectionsEffects } from "./core/ngrx/effects/connection.effects";
import { OrganizationEffects } from "./core/ngrx/effects/organization.effects";
import { ProjectEffects } from "./core/ngrx/effects/project.effects";
import { ReleaseEffects } from "./core/ngrx/effects/release.effects";
import { TestReportSummarizationEffects } from "./core/ngrx/effects/report-summarization.effects";
import { TestPrioritizationEffects } from "./core/ngrx/effects/test-prioritization.effects";
import { TestCaseGeneratorEffects } from "./core/ngrx/effects/testcase-generator.effects";
import { appReducer } from "./core/ngrx/states/app.state";
import { authInterceptor } from "./core/services/auth.interceptor";
import { ConfigService } from "./core/services/config/config.service";
import { initializeKeycloak } from "./core/services/keycloak/keycloak-init";
import { UtilitiesService } from "./core/services/utilities.service";
import { PrimeNgModule } from "./core/shared/primeng.module";

registerLocaleData(en);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor])),
    MessageService,
    provideStore(appReducer),
    provideEffects([
      OrganizationEffects,
      ProjectEffects,
      ConnectionsEffects,
      TestReportSummarizationEffects,
      ApplicationDetailsEffects,
      TestCaseGeneratorEffects,
      TestPrioritizationEffects,
      AutomationEffects,
      AgentEffects,
      ReleaseEffects
    ]),
    DatePipe,
    provideRouterStore(),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: false,
      trace: true,
    }),
    importProvidersFrom(
      HttpClientModule,
      KeycloakAngularModule,
      BlockUIModule.forRoot(),
      PrimeNgModule
    ),
    ConfigService,
    KeycloakService,
    UtilitiesService,
    {
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigService) => () =>
        configService.loadRuntimeConfig(),
      deps: [ConfigService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      deps: [KeycloakService, ConfigService, UtilitiesService],
      multi: true,
    },
    provideNzI18n(en_US),
    importProvidersFrom(FormsModule),
    provideAnimationsAsync(),
    provideHttpClient(),
  ],
};
